import { type MouseEvent, useEffect, useMemo, useState } from "react";
import { ArrowRight, Unplug, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { useSession } from "@/frontend/lib/auth";
import { useInfraStore } from "@/frontend/stores/infraStore";
import type { DisconnectedChannel } from "@/frontend/hooks/useDashboard";
import {
  getChannelHealthDismissalKey,
  getChannelOutageId,
  getDisconnectedChannelAccountLabel,
  getChannelRecoveryActionLabel,
} from "@/frontend/lib/channel-health";

export function WhatsAppConnectionBanner({
  orgId,
  channels,
  isPreview = false,
}: {
  orgId: string;
  channels: DisconnectedChannel[];
  isPreview?: boolean;
}) {
  const { data: session } = useSession();
  const { pathname } = useLocation();
  const userId = session?.user?.id;
  const storageKey = userId
    ? getChannelHealthDismissalKey(orgId, userId)
    : null;
  const [dismissedOutages, setDismissedOutages] = useState<string[]>([]);
  const [loadedStorageKey, setLoadedStorageKey] = useState<string | null>(null);

  useEffect(() => {
    if (!storageKey) {
      setDismissedOutages([]);
      setLoadedStorageKey(null);
      return;
    }
    try {
      const stored = JSON.parse(window.localStorage.getItem(storageKey) || "[]");
      setDismissedOutages(Array.isArray(stored) ? stored.filter((item): item is string => typeof item === "string") : []);
    } catch {
      setDismissedOutages([]);
    }
    setLoadedStorageKey(storageKey);
  }, [storageKey]);

  const visibleChannels = useMemo(() => {
    const dismissed = new Set(dismissedOutages);
    return channels.filter((channel) => !dismissed.has(getChannelOutageId(channel)));
  }, [channels, dismissedOutages]);

  if (!userId || loadedStorageKey !== storageKey || visibleChannels.length === 0) return null;

  const first = visibleChannels[0];
  const multiple = visibleChannels.length > 1;
  const accountLabel = multiple
    ? null
    : getDisconnectedChannelAccountLabel(first);
  const href = multiple
    ? `/org/${orgId}/app/settings/channels`
    : `/org/${orgId}/app/settings/${first.settingsSection}`;
  const belongsToAnotherAdmin = first.type === "whatsapp" &&
    first.ownerUserId && first.ownerUserId !== userId;
  const heading = multiple
    ? `${visibleChannels.length} channels are disconnected`
    : belongsToAnotherAdmin
      ? `${first.name || "A team member"}'s WhatsApp is disconnected`
      : `${first.label} is disconnected`;
  const impact = multiple
    ? "Messages and automations using these channels are paused until they reconnect."
    : belongsToAnotherAdmin
      ? `This affects only ${first.name || "this team member"}'s linked WhatsApp—not every connection in the workspace.`
      : `Messages and automations${accountLabel ? ` for ${accountLabel}` : " for this account"} are paused until it reconnects.`;
  // Linking to the page you're already on does nothing, so take the person to
  // the reconnect controls and make sure they reflect the bridge's real state.
  const handleRecoveryClick = (event: MouseEvent) => {
    if (pathname.replace(/\/$/, "") !== href) return;
    event.preventDefault();
    if (!multiple && first.settingsSection === "whatsapp-cloud") {
      void useInfraStore.getState().fetchStatus(orgId);
      document.getElementById("whatsapp-cloud-connection")?.scrollIntoView({ behavior: "smooth" });
      return;
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const handleDismiss = () => {
    const nextDismissed = Array.from(new Set([
      ...dismissedOutages,
      ...visibleChannels.map(getChannelOutageId),
    ]));
    if (storageKey) {
      try {
        window.localStorage.setItem(storageKey, JSON.stringify(nextDismissed));
      } catch {
        // The in-memory dismissal still works when browser storage is blocked.
      }
    }
    setDismissedOutages(nextDismissed);
  };

  return (
    <div
      role="alert"
      className="border-b border-border bg-card px-4 py-3 shadow-[inset_3px_0_0_0_theme(colors.orange.500)]"
    >
      <div className="relative mx-auto flex max-w-5xl flex-col gap-3 pr-12 sm:flex-row sm:items-center sm:justify-between sm:gap-5 sm:pr-0">
        <div className="flex min-w-0 items-start gap-3 sm:items-center">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-orange-100 text-orange-700 dark:bg-orange-400/10 dark:text-orange-300">
            <Unplug className="h-[18px] w-[18px]" />
          </span>
          <div className="min-w-0 text-sm">
            <p className="font-semibold text-foreground">
              {isPreview && (
                <span className="mr-1.5 text-[10px] font-bold uppercase tracking-[0.12em] text-orange-700 dark:text-orange-300">
                  Preview ·
                </span>
              )}
              {heading}
            </p>
            <p className="mt-0.5 leading-5 text-muted-foreground">
              {impact}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1 sm:shrink-0">
          <Link
            to={href}
            onClick={handleRecoveryClick}
            className="group inline-flex min-h-10 items-center gap-1.5 rounded-lg bg-orange-600 px-3.5 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-orange-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 focus-visible:ring-offset-2 dark:bg-orange-500 dark:text-orange-950 dark:hover:bg-orange-400"
          >
            {multiple
              ? "Review channels"
              : getChannelRecoveryActionLabel(first, Boolean(belongsToAnotherAdmin))}
            <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
          </Link>
        </div>
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Dismiss disconnected channels alert"
          title="Dismiss for this outage"
          className="absolute right-1 top-1 flex h-10 w-10 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-orange-600 sm:static"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
